import Model from '@/store/plugins/orm/Model'
import HashGen from '@/utilities/HashGen';
import Money from '@/wrappers/Money';
import i18n from '@/i18n';
import { Product } from './ModelRepository';

export class PriceCalculationItem extends Model {
    static get entity () {
        return 'price_calculation_items'
    }

    static fields () {
        return {
            id: this.attr(null),
            price_calculation_id: this.attr(null),
            date: this.attr(null),
            product_id: this.attr(null),
            unit_of_measure: this.attr(null),
            tax_rate: this.attr('0.00', value => _.round(value, 2).toFixed(2)),
            quantity: this.attr('1.000', value => _.round(value, 3).toFixed(3)),
            invoice_date: this.attr(null),
            invoice_price: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            invoice_discount_percent: this.attr('0.00', value => _.round(value, 2).toFixed(2)),
            invoice_with_tax: this.attr(true, value => !!value),
            dependent_costs: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            selling_price: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            selling_price_after_tax: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            margin_percent: this.attr('0.00', value => _.round(value, 2).toFixed(2)),
            recorded_at: this.attr(null),

            hash: this.attr(HashGen.regular()),
        }
    }

    getTaxRate() {
        return parseFloat(this.tax_rate);
    }

    getQuantity() {
        return parseFloat(this.quantity);
    }

    getInvoicePrice() {
        return Money.parse(this.invoice_price);
    }

    getInvoiceDiscountPercent() {
        return parseFloat(this.invoice_discount_percent);
    }

    getInvoicePriceAfterDiscount() {
        const value = this.getInvoicePrice()
            .multiply(1 - this.getInvoiceDiscountPercent() / 100);

        return Money.parse(value.toFormat('0.0000'));
    }

    getInvoiceValue() {
        const value = this.getInvoicePriceAfterDiscount()
            .multiply(this.getQuantity())

        return Money.parse(value.toFormat('0.0000'));
    }

    getDependentCosts() {
        return Money.parse(this.dependent_costs);
    }

    getPurchaseValue() {
        const value = this.getInvoiceValue()
            .add(this.getDependentCosts());

        return Money.parse(value.toFormat('0.0000'));
    }

    getPurchaseTax() {
        if(!this.invoice_with_tax) return Money.parse(0);

        const value = this.getPurchaseValue()
            .multiply(this.getTaxRate() / 100);

        return Money.parse(value.toFormat('0.0000'));
    }

    getPurchaseValueAfterTax() {
        const value = this.getPurchaseValue()
            .add(this.getPurchaseTax());

        return Money.parse(value.toFormat('0.0000'));
    }

    getPriceDifference() {
        return this.getSellingValue()
            .subtract(this.getPurchaseValue());
    }

    getPriceDifferencePerUnit() {
        const value = this.getQuantity() != 0
            ? this.getPriceDifference()
                .divide(this.getQuantity())
            : Money.parse(0);

        return Money.parse(value.toFormat('0.0000'));
    }

    getSellingValue() {
        const value = this.getSellingPrice()
            .multiply(this.getQuantity());

        return Money.parse(value.toFormat('0.0000'));
    }

    getTax() {
        const value = this.getSellingValue()
            .multiply(this.getTaxRate() / 100);

        return Money.parse(value.toFormat('0.0000'));
    }

    getSellingValueAfterTax() {
        const value = this.getSellingValue()
            .add(this.getTax());

        return Money.parse(value.toFormat('0.0000'));
    }

    getPurchasePrice() {
        const value = this.getQuantity() != 0
            ? this.getPurchaseValue()
                .divide(this.getQuantity())
            : Money.parse(0);

        return Money.parse(value.toFormat('0.0000'));
    }

    getPurchasePriceAfterTax() {
        if(!this.invoice_with_tax) return this.getPurchasePrice();

        const value = this.getPurchasePrice()
            .multiply(1 + this.getTaxRate() / 100);

        return Money.parse(value.toFormat('0.0000'));
    }

    getSellingPrice() {
        return Money.parse(this.selling_price);
    }

    getSellingPriceAfterTax() {
        return Money.parse(this.selling_price_after_tax);
    }

    getMarginPercent() {
        return parseFloat(this.margin_percent);
    }

    getProduct() {
        return Product.all().find(product => product.id === this.product_id);
    }

    getUnitOfMeasure() {
        return i18n.t(`products.unit_of_measure.${this.unit_of_measure}`) !== `products.unit_of_measure.${this.unit_of_measure}`
            ? i18n.t(`products.unit_of_measure.${this.unit_of_measure}`)
            : this.unit_of_measure
    }

    calculatePrices(value, baseField) {
        let sellingPrice = Money.parse(0);
        let sellingPriceAfterTax = Money.parse(0);
        let marginPercent = Money.parse(0);

        if(baseField === 'selling_price') {
            sellingPrice = Money.parse(_.round(value, 4));

            sellingPriceAfterTax = Money.parse(sellingPrice.toUnit())
                .multiply(1 + this.getTaxRate() / 100);

            marginPercent = this.getPurchasePrice().toUnit() != 0
                ? Money.parse(sellingPrice.toUnit())
                    .subtract(this.getPurchasePrice())
                    .divide(this.getPurchasePrice().toUnit())
                    .multiply(100)
                : Money.parse(0);
        }

        else if(baseField === 'selling_price_after_tax') {
            sellingPriceAfterTax = Money.parse(_.round(value, 4));

            sellingPrice = Money.parse(sellingPriceAfterTax.toUnit())
                .divide(1 + this.getTaxRate() / 100);

            marginPercent = this.getPurchasePrice().toUnit() != 0
                ? Money.parse(sellingPrice.toUnit())
                    .subtract(this.getPurchasePrice())
                    .divide(this.getPurchasePrice().toUnit())
                    .multiply(100)
                : Money.parse(0);
        }

        else if(baseField === 'margin_percent') {
            marginPercent = Money.parse(_.round(value, 2));

            sellingPrice = this.getPurchasePrice()
                .multiply(1 + marginPercent.toUnit() / 100);

            sellingPriceAfterTax = Money.parse(sellingPrice.toUnit())
                .multiply(1 + this.getTaxRate() / 100);
        }

        return {
            selling_price: sellingPrice.toFormat('0.0000'),
            selling_price_after_tax: sellingPriceAfterTax.toFormat('0.0000'),
            margin_percent: marginPercent.toFormat('0.00'),
        }
    }

    calculateDependentCosts(totalInvoicePrice = null, totalDependentCosts = null) {
        if(!totalInvoicePrice || !totalDependentCosts) return '0.0000';

        const value = this.getInvoiceValue()
            .divide(_.round(totalInvoicePrice, 4))
            .multiply(_.round(totalDependentCosts, 4));

        return value.toFormat('0.0000');
    }
}

export default PriceCalculationItem
